import { useEffect, useMemo, useRef } from "react";
import { useFormContext } from "react-hook-form";
import { useDispatch, useSelector } from "src/redux/hooks";
import { setShipsTime } from "src/redux/slices/generatorSlice";

import {
  applyShipsTimeOffsetToEntryLtRows,
  formatShipsTimeDisplay,
  getShipTimeFieldIds,
  parseShipsTimeOffset,
} from "./NMEAAutomationModal.utils";

export const useSyncEntryShipsTimeOffset = (enabled: boolean) => {
  const dispatch = useDispatch();
  const { setValue, getValues, watch } = useFormContext();
  const sections = useSelector(s => s.generatorSlice.lbentry.sections);
  const nmeaData = useSelector(s => s.generatorSlice.lbentry.NMEAData);
  const shipsTime = useSelector(s => s.generatorSlice.lbentry.shipsTime);
  const isInitialShipsTimeSync = useRef(true);

  const shipTimeFieldIds = useMemo(
    () => (enabled ? getShipTimeFieldIds(sections) : []),
    [sections, enabled],
  );

  useEffect(() => {
    if (enabled) {
      isInitialShipsTimeSync.current = true;
      return;
    }

    dispatch(setShipsTime(null));
  }, [dispatch, enabled]);

  useEffect(() => {
    if (!enabled) return;

    if (isInitialShipsTimeSync.current) {
      isInitialShipsTimeSync.current = false;
      return;
    }

    applyShipsTimeOffsetToEntryLtRows({
      sections,
      nmeaData,
      shipsTimeOffset: shipsTime,
      getValues,
      setValue,
    });
  }, [enabled, getValues, nmeaData, sections, setValue, shipsTime]);

  useEffect(() => {
    if (!enabled || shipTimeFieldIds.length === 0) {
      return;
    }

    const syncField = (name: string) => {
      const currentValue = getValues(name);
      if (currentValue === "") {
        dispatch(setShipsTime(null));
        return;
      }

      const parsed = parseShipsTimeOffset(currentValue);
      if (parsed !== null) {
        const formattedValue = formatShipsTimeDisplay(parsed);
        if (currentValue !== formattedValue) {
          setValue(name, formattedValue);
        }
        dispatch(setShipsTime(parsed));
      }
    };

    const syncFromEntryFields = () => {
      for (const fieldId of shipTimeFieldIds) {
        const currentValue = getValues(fieldId);
        if (currentValue !== "" && currentValue != null) {
          syncField(fieldId);
          return;
        }
      }

      if (shipTimeFieldIds.every(fieldId => !getValues(fieldId))) {
        dispatch(setShipsTime(null));
      }
    };

    syncFromEntryFields();

    const subscription = watch((_, { name }) => {
      if (!name || !shipTimeFieldIds.includes(name)) return;
      syncField(name);
    });

    return () => subscription.unsubscribe();
  }, [dispatch, enabled, getValues, setValue, shipTimeFieldIds, watch]);

  return shipTimeFieldIds;
};
